import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useVvsFarmBoostContract } from 'hooks/useContract'
import { useMutation, useQueryClient } from 'react-query'
import { refreshFarmUserDataFromPid } from 'state/farms/useDeserializedFarmsUser'
import { getFarmVersionFromPid } from 'state/farms/farmsPidToVersion'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import { useUserMoleTokenIds } from 'state/minerMole/useUserMoleTokenIds'
import { QueryKey } from 'config/constants/queryKey'
import { ContractTransaction } from 'ethers'

export const useHarvestBoostByPid = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(
    async (farmId: number) => {
      const tx = await vvsFarmBoostContract['harvestBoostAndWithdraw(uint256)'](farmId)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: (_data, farmId) => {
        refreshFarmUserDataFromPid(farmId, getFarmVersionFromPid(farmId))
        useUserBoostedFarmsInfos.getState().refresh()
      },
    },
  )
}

export const useMoveBoost = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(
    async (params: { oldPid: number; newPid: number }) => {
      const { oldPid, newPid } = params

      const tx = await vvsFarmBoostContract.moveBoost(oldPid, newPid)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        useUserBoostedFarmsInfos.getState().refresh()
      },
    },
  )
}

export const useBoostMultipleFarms = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  return useMutation(
    async (params: {
      boostInfoList: Array<{
        pid: number
        nfts: any[]
      }>
    }) => {
      const { boostInfoList } = params

      const tx = await vvsFarmBoostContract.boostMultipleFarms(boostInfoList)
      const receipt = await tx.wait()

      return { receipt, tx }
    },
    {
      onSuccess: () => {
        useUserBoostedFarmsInfos.getState().refresh()
      },
    },
  )
}

export const useStakeNftsAndBoostFarm = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await vvsFarmBoostContract.stakeNftsAndBoostFarm(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        useUserBoostedFarmsInfos.getState().refresh()
        useUserMoleTokenIds.getState().refresh()
      },
    },
  )
}

export const useUnStakeNfts = () => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number; nfts: any[] }) => {
      const { farmId, nfts } = params

      const tx = await vvsFarmBoostContract.unstakeNfts(farmId, nfts)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        useUserBoostedFarmsInfos.getState().refresh()
        useUserMoleTokenIds.getState().refresh()
      },
    },
  )
}

export const useUpdateShares = (
  settleCallback?: () => void,
  successCallBack?: (tx: ContractTransaction) => void,
  errorCallback?: () => void,
) => {
  const vvsFarmBoostContract = useVvsFarmBoostContract()
  const { account } = useActiveWeb3React()
  const queryClient = useQueryClient()

  return useMutation(
    async (params: { farmId: number }) => {
      const { farmId } = params

      const tx = await vvsFarmBoostContract['updateShares(address,uint256)'](account, farmId)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: ({ tx }) => {
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        useUserBoostedFarmsInfos.getState().refresh()
        useUserMoleTokenIds.getState().refresh()
        successCallBack?.(tx)
      },
      onSettled: () => {
        settleCallback?.()
      },
      onError: () => {
        errorCallback?.()
      },
    },
  )
}
